import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc,
  query, orderBy, Timestamp, where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { ADMIN_ROLES, MANAGE_ROLES } from '../../utils/roles';
import TaskSummary from './components/TaskSummary';
import TaskFilters from './components/TaskFilters';
import TaskCard    from './components/TaskCard';
import TaskModal   from './components/TaskModal';
import './Tasks.css';

// Role hierarchy level — used for task-level visibility
const ROLE_LEVEL = { owner: 3, admin: 2, manager: 1, employee: 0 };

// Returns true if `viewerRole` is allowed to see a task assigned to `assigneeRole`
function canViewTask(viewerRole, assigneeRole) {
  const vLevel = ROLE_LEVEL[viewerRole] ?? 0;
  // If assignedToRole is missing (legacy task created before this field existed),
  // default to 'manager' level so all management roles can see it.
  // New tasks always have assignedToRole set correctly on save.
  const aLevel = assigneeRole != null ? (ROLE_LEVEL[assigneeRole] ?? 0) : ROLE_LEVEL['manager'];
  return vLevel >= aLevel;
}

const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };

const DEFAULT_FORM = {
  title: '', description: '', assignedTo: '', assignedToName: '',
  priority: 'medium', category: 'Kitchen', dueDate: '', notes: '',
};

export default function TaskDashboard() {
  const { user, role } = useAuth();
  const isAdmin    = MANAGE_ROLES.includes(role); // owner + admin + manager get management view
  const canAssign  = ADMIN_ROLES.includes(role);  // only owner + admin can create/assign tasks

  // Data
  const [tasks,   setTasks]   = useState([]);
  const [staff,   setStaff]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  // Filters
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');

  // Modal
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  // Load staff list for the assign dropdown (admin only)
  const loadStaff = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = snap.docs
        .map(d => ({ uid: d.id, ...d.data() }))
        .filter(u => ['employee', 'manager', 'admin', 'owner'].includes(u.role))
        .map(u => ({ uid: u.uid, name: u.name || u.email, role: u.role }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setStaff(list);
    } catch (err) {
      console.error('loadStaff:', err);
    }
  }, [isAdmin]);

  // Load tasks — all tasks for admin, own tasks for employee
  const loadTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const q = isAdmin
        ? query(collection(db, 'tasks'), orderBy('createdAt', 'desc'))
        : query(collection(db, 'tasks'), where('assignedTo', '==', user.uid), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('loadTasks:', err);
    }
    setLoading(false);
  }, [user, isAdmin, role]);

  useEffect(() => { loadStaff(); }, [loadStaff]);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Role-hierarchy filtered task list:
  // For each task, resolve the assignee's role — use stored assignedToRole if present,
  // otherwise cross-reference the staff list (fixes legacy tasks created before this field existed).
  // If still unknown, only owner/admin can see it.
  const visibleTasks = React.useMemo(() => {
    if (!isAdmin) return tasks; // employees already get only their own tasks from Firestore
    return tasks.filter(t => {
      const resolvedRole = t.assignedToRole
        ?? staff.find(s => s.uid === t.assignedTo)?.role
        ?? 'owner';
      return canViewTask(role, resolvedRole);
    });
  }, [tasks, staff, role, isAdmin]);

  // Filtered + priority-sorted task list
  const filtered = visibleTasks
    .filter(t => {
      if (statusFilter   !== 'all' && t.status     !== statusFilter)   return false;
      if (priorityFilter !== 'all' && t.priority   !== priorityFilter) return false;
      if (assigneeFilter !== 'all' && t.assignedTo !== assigneeFilter) return false;
      return true;
    })
    .sort((a, b) => {
      const diff = (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99);
      return diff !== 0 ? diff : (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
    });

  const counts = {
    pending:       visibleTasks.filter(t => t.status === 'pending').length,
    'in-progress': visibleTasks.filter(t => t.status === 'in-progress').length,
    completed:     visibleTasks.filter(t => t.status === 'completed').length,
    urgent:        visibleTasks.filter(t => t.priority === 'urgent' && t.status !== 'completed').length,
  };

  const openCreate = () => {
    setEditingTask(null);
    setForm(DEFAULT_FORM);
    setModalOpen(true);
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setForm({
      title:          task.title || '',
      description:    task.description || '',
      assignedTo:     task.assignedTo || '',
      assignedToName: task.assignedToName || '',
      priority:       task.priority || 'medium',
      category:       task.category || 'Kitchen',
      dueDate:        task.dueDate ? task.dueDate.toDate().toISOString().slice(0, 16) : '',
      notes:          task.notes || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingTask(null);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'assignedTo') {
        next.assignedToName = staff.find(s => s.uid === value)?.name || '';
      }
      return next;
    });
  };

  // Create or update a task
  const handleSave = async () => {
    if (!form.title.trim() || !form.assignedTo) return;
    setSaving(true);
    try {
      const assignee = staff.find(s => s.uid === form.assignedTo);
      const payload = {
        title:          form.title.trim(),
        description:    form.description.trim(),
        assignedTo:     form.assignedTo,
        assignedToName: form.assignedToName,
        assignedToRole: assignee?.role || 'employee',
        priority:       form.priority,
        category:       form.category,
        notes:          form.notes.trim(),
        dueDate:        form.dueDate ? Timestamp.fromDate(new Date(form.dueDate)) : null,
        updatedAt:      Timestamp.now(),
      };
      if (editingTask) {
        await updateDoc(doc(db, 'tasks', editingTask.id), payload);
      } else {
        await addDoc(collection(db, 'tasks'), {
          ...payload,
          status:        'pending',
          createdAt:     Timestamp.now(),
          createdByUid:  user.uid,
          createdByName: user.displayName || user.email,
        });
      }
      closeModal();
      await loadTasks();
    } catch (err) {
      console.error('handleSave:', err);
    }
    setSaving(false);
  };

  // Move a task through its status lifecycle (pending -> in-progress -> completed)
  const handleStatusChange = async (taskId, status) => {
    try {
      const patch = { status, updatedAt: Timestamp.now() };
      if (status === 'completed') patch.completedAt = Timestamp.now();
      await updateDoc(doc(db, 'tasks', taskId), patch);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...patch } : t));
    } catch (err) {
      console.error('handleStatusChange:', err);
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    await deleteDoc(doc(db, 'tasks', taskId));
    setTasks(prev => prev.filter(t => t.id !== taskId));
  };

  return (
    <div className="tasks-page">

      {/* Hero */}
      <div className="tasks-hero">
        <div className="tasks-hero__left">
          <div className="tasks-hero__tag">📋 Operations</div>
          <h1 className="tasks-hero__title">
            {isAdmin ? 'Task Management' : 'My Tasks'}
          </h1>
          <p className="tasks-hero__sub">
            {isAdmin
              ? 'Track tasks across your team'
              : 'Tasks assigned to you — keep it moving!'}
          </p>
        </div>
          {canAssign && (
          <button className="tasks-hero__btn" onClick={openCreate}>
            ＋ Assign Task
          </button>
        )}
      </div>

      <TaskSummary counts={counts} />

      <TaskFilters
        statusFilter={statusFilter}     onStatusChange={setStatusFilter}
        priorityFilter={priorityFilter} onPriorityChange={setPriorityFilter}
        assigneeFilter={assigneeFilter} onAssigneeChange={setAssigneeFilter}
        staff={staff}
        isAdmin={isAdmin}
      />

      {loading ? (
        <div className="tasks-loading">
          <div className="tasks-loading__spinner" />
        </div>
      ) : (
        <div className="tasks-grid">
          {filtered.length === 0 ? (
            <div className="tasks-empty">
              <div className="tasks-empty__icon">✅</div>
              <div className="tasks-empty__title">No tasks here</div>
              <div className="tasks-empty__sub">
                {canAssign
                  ? 'Click &quot;+ Assign Task&quot; to create one.'
                  : isAdmin
                  ? 'No tasks visible at your access level.'
                  : 'No tasks assigned to you yet.'}
              </div>
            </div>
          ) : (
            filtered.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                isAdmin={isAdmin}
                canAssign={canAssign}
                onEdit={openEdit}
                onDelete={handleDelete}
                onStatusChange={handleStatusChange}
              />
            ))
          )}
        </div>
      )}

      <TaskModal
        open={modalOpen}
        onClose={closeModal}
        editingTask={editingTask}
        form={form}
        onChange={handleFormChange}
        onSave={handleSave}
        saving={saving}
        staff={staff}
      />

    </div>
  );
}
